import { Action, createReducer, on } from '@ngrx/store';
import { MetadataKind } from '../../../metadata-mgmt/models/Metadata-mgmt';
import {
  ICrossFieldRule,
  IRuleOperation,
  IRuleTypeCatalogEntry,
  IRuleTypeDefinition,
  IValidatorRule,
} from '../../models/validation-rule';
import * as Actions from '../actions';

export interface ValidationRulesState {
  fieldRules: IValidatorRule[];
  crossFieldRules: ICrossFieldRule[];
  catalog: IRuleTypeCatalogEntry[];
  // Configurable catalog tables — listed in their entirety so the Rule
  // Types tab can present a flat view. The cross-product with `kind` is
  // resolved by selectors / view code, not stored separately.
  operations: IRuleOperation[];
  ruleTypeDefs: IRuleTypeDefinition[];
  // null sourceType => Core scope (templates with source_type=null)
  selectedSourceTypeId: number | null;
  selectedTemplateId: number | null;
  // Baseline vs post-analysis scope for the template list + rule pickers.
  selectedKind: MetadataKind;
  loading: {
    fieldRules: boolean;
    crossFieldRules: boolean;
    catalog: boolean;
    operations: boolean;
    ruleTypeDefs: boolean;
  };
}

const initialState: ValidationRulesState = {
  fieldRules: [],
  crossFieldRules: [],
  catalog: [],
  operations: [],
  ruleTypeDefs: [],
  selectedSourceTypeId: null,
  selectedTemplateId: null,
  selectedKind: 'BASELINE',
  loading: {
    fieldRules: false,
    crossFieldRules: false,
    catalog: false,
    operations: false,
    ruleTypeDefs: false,
  },
};

const reducer = createReducer(
  initialState,

  // ---- loading flips ------------------------------------------------
  on(Actions.ValidationRulesUiActions.LoadFieldRules, (state) => ({
    ...state,
    loading: { ...state.loading, fieldRules: true },
  })),
  on(Actions.ValidationRulesApiActions.loadFieldRulesSuccess, (state, { payload }) => ({
    ...state,
    fieldRules: payload,
    loading: { ...state.loading, fieldRules: false },
  })),
  on(Actions.ValidationRulesApiActions.loadFieldRulesError, (state) => ({
    ...state,
    loading: { ...state.loading, fieldRules: false },
  })),

  on(Actions.ValidationRulesUiActions.LoadCrossFieldRules, (state) => ({
    ...state,
    loading: { ...state.loading, crossFieldRules: true },
  })),
  on(Actions.ValidationRulesApiActions.loadCrossFieldRulesSuccess, (state, { payload }) => ({
    ...state,
    crossFieldRules: payload,
    loading: { ...state.loading, crossFieldRules: false },
  })),
  on(Actions.ValidationRulesApiActions.loadCrossFieldRulesError, (state) => ({
    ...state,
    loading: { ...state.loading, crossFieldRules: false },
  })),

  on(Actions.ValidationRulesUiActions.LoadCatalog, (state) => ({
    ...state,
    loading: { ...state.loading, catalog: true },
  })),
  on(Actions.ValidationRulesApiActions.loadCatalogSuccess, (state, { payload }) => ({
    ...state,
    catalog: payload,
    loading: { ...state.loading, catalog: false },
  })),
  on(Actions.ValidationRulesApiActions.loadCatalogError, (state) => ({
    ...state,
    loading: { ...state.loading, catalog: false },
  })),

  // ---- field-rule mutations ----------------------------------------
  on(Actions.ValidationRulesApiActions.createFieldRuleSuccess, (state, { payload }) => ({
    ...state,
    fieldRules: [...state.fieldRules, payload],
  })),
  on(Actions.ValidationRulesApiActions.patchFieldRuleSuccess, (state, { payload }) => ({
    ...state,
    fieldRules: state.fieldRules.map((r) => (r.id === payload.id ? { ...r, ...payload } : r)),
  })),
  on(Actions.ValidationRulesApiActions.deleteFieldRuleSuccess, (state, { id }) => ({
    ...state,
    fieldRules: state.fieldRules.filter((r) => r.id !== id),
  })),

  // ---- cross-field mutations --------------------------------------
  on(Actions.ValidationRulesApiActions.createCrossFieldRuleSuccess, (state, { payload }) => ({
    ...state,
    crossFieldRules: [...state.crossFieldRules, payload],
  })),
  on(Actions.ValidationRulesApiActions.patchCrossFieldRuleSuccess, (state, { payload }) => ({
    ...state,
    crossFieldRules: state.crossFieldRules.map((r) =>
      r.id === payload.id ? { ...r, ...payload } : r,
    ),
  })),
  on(Actions.ValidationRulesApiActions.deleteCrossFieldRuleSuccess, (state, { id }) => ({
    ...state,
    crossFieldRules: state.crossFieldRules.filter((r) => r.id !== id),
  })),

  // ---- selection ---------------------------------------------------
  on(Actions.ValidationRulesUiActions.SelectSourceType, (state, { sourceTypeId }) => ({
    ...state,
    selectedSourceTypeId: sourceTypeId,
    // Changing source type clears any template selection, since the template
    // belongs to one scope at a time.
    selectedTemplateId: null,
  })),
  on(Actions.ValidationRulesUiActions.SelectTemplate, (state, { templateId }) => ({
    ...state,
    selectedTemplateId: templateId,
  })),
  on(Actions.ValidationRulesUiActions.SelectKind, (state, { kind }) => ({
    ...state,
    selectedKind: kind,
    // A template belongs to a single kind, so switching scope clears the
    // current selection to avoid editing rules against a now-hidden template.
    selectedTemplateId: null,
  })),

  // ---- configurable catalog: operations -----------------------------
  on(Actions.ValidationRulesUiActions.LoadOperations, (state) => ({
    ...state,
    loading: { ...state.loading, operations: true },
  })),
  on(Actions.ValidationRulesApiActions.loadOperationsSuccess, (state, { payload }) => ({
    ...state,
    operations: payload,
    loading: { ...state.loading, operations: false },
  })),
  on(Actions.ValidationRulesApiActions.loadOperationsError, (state) => ({
    ...state,
    loading: { ...state.loading, operations: false },
  })),

  // ---- configurable catalog: rule type definitions ------------------
  on(Actions.ValidationRulesUiActions.LoadRuleTypeDefinitions, (state) => ({
    ...state,
    loading: { ...state.loading, ruleTypeDefs: true },
  })),
  on(Actions.ValidationRulesApiActions.loadRuleTypeDefsSuccess, (state, { payload }) => ({
    ...state,
    ruleTypeDefs: payload,
    loading: { ...state.loading, ruleTypeDefs: false },
  })),
  on(Actions.ValidationRulesApiActions.loadRuleTypeDefsError, (state) => ({
    ...state,
    loading: { ...state.loading, ruleTypeDefs: false },
  })),

  on(Actions.ValidationRulesApiActions.createRuleTypeDefSuccess, (state, { payload }) => ({
    ...state,
    ruleTypeDefs: [...state.ruleTypeDefs, payload],
  })),
  on(Actions.ValidationRulesApiActions.patchRuleTypeDefSuccess, (state, { payload }) => ({
    ...state,
    ruleTypeDefs: state.ruleTypeDefs.map((d) => (d.id === payload.id ? { ...d, ...payload } : d)),
  })),
  on(Actions.ValidationRulesApiActions.deleteRuleTypeDefSuccess, (state, { id }) => ({
    ...state,
    ruleTypeDefs: state.ruleTypeDefs.filter((d) => d.id !== id),
  })),

  // ---- param definitions (nested inside a RuleTypeDefinition) ------
  //
  // Param defs are not stored separately; they are nested under their
  // parent rule type def. Mutations therefore merge into the parent's
  // `param_defs` array so the UI's tree view stays consistent without
  // an extra refetch.
  on(Actions.ValidationRulesApiActions.createParamDefSuccess, (state, { payload }) => ({
    ...state,
    ruleTypeDefs: state.ruleTypeDefs.map((d) =>
      d.id === payload.rule_type
        ? { ...d, param_defs: [...d.param_defs, payload] }
        : d,
    ),
  })),
  on(Actions.ValidationRulesApiActions.patchParamDefSuccess, (state, { payload }) => ({
    ...state,
    ruleTypeDefs: state.ruleTypeDefs.map((d) =>
      d.id === payload.rule_type
        ? {
            ...d,
            param_defs: d.param_defs.map((p) => (p.id === payload.id ? { ...p, ...payload } : p)),
          }
        : d,
    ),
  })),
  on(Actions.ValidationRulesApiActions.deleteParamDefSuccess, (state, { id }) => ({
    ...state,
    ruleTypeDefs: state.ruleTypeDefs.map((d) => ({
      ...d,
      param_defs: d.param_defs.filter((p) => p.id !== id),
    })),
  })),
);

export function ValidationRulesReducer(
  state: ValidationRulesState | undefined,
  action: Action,
): ValidationRulesState {
  return reducer(state, action);
}
